import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import { GenericForm } from "common/Components/GenericForm";
import CustomerAccessEditor from "./CustomerAccessEditor";
import {
  buildActiveCustomerSelectOptions,
  CUSTOMER_ACCESS_STATUS,
  enrichCustomerAccessRowFromActiveCustomer,
  hasCustomerAccessChanged,
  hasPlatformRoleChanged,
  isPlatformUserAccountEditable,
  normalizeCustomerAccessStatus,
  PLATFORM_USER_ACCESS_UI,
  isPlatformAdminPlatformRole,
  normalizePlatformRoleValue,
  shouldShowPlatformAdminOptionalCustomerHint,
  validatePlatformCustomerAccess,
} from "../PlatformUserAccess.constants";
import {
  DetailPanelBody,
  DetailPanelContainer,
  DetailPanelCustomerAccessSection,
  DetailPanelEmptyStateText,
  DetailPanelFooter,
  DetailPanelInlineAlert,
  DetailPanelPlatformRoleSection,
} from "./UserAccessDetailPanel.styles";

const createEmptyCustomerAccess = () => ({
  customerId: "",
  roles: [],
  accessStatus: "",
});

const mapRoleNamesToOptionValues = (roles, roleOptions) =>
  (roles ?? [])
    .map((role) => {
      const key = typeof role === "string" ? role : role?.value || role?.label;
      if (key == null || String(key).trim() === "") {
        return null;
      }
      const match = roleOptions.find(
        (option) =>
          String(option?.value) === String(key) ||
          String(option?.label) === String(key)
      );
      return match?.value ?? key;
    })
    .filter(Boolean);

const mapAccessToRow = (access, roleOptions = []) => ({
  accessId: access.accessId,
  customerId:
    access.customerId != null && access.customerId !== ""
      ? access.customerId
      : "",
  roles: mapRoleNamesToOptionValues(access.roles, roleOptions),
  accessStatus:
    normalizeCustomerAccessStatus(access.accessStatus ?? access.status) ||
    CUSTOMER_ACCESS_STATUS.ACTIVE,
  customerCode: access.customerCode,
  customerName: access.customerName,
});

const platformFormFields = (platformRoleOptions, disabled) => [
  {
    name: "platformRole",
    label: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
    type: "singleSelect",
    options: platformRoleOptions,
    placeholder: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
    disableClearable: true,
    disabled,
  },
];

const UserAccessDetailPanel = ({
  selectedUser,
  customers,
  platformRoleOptions,
  roleOptions,
  onSave,
  onCancel,
  onAccessActionSuccess,
  onCustomerUserListAction,
}) => {
  const [formValues, setFormValues] = useState({
    platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
  });
  const [customerAccessRows, setCustomerAccessRows] = useState([]);
  const [baseline, setBaseline] = useState({
    platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
    customerAccessRows: [],
  });
  const [validationError, setValidationError] = useState("");
  const [rowErrors, setRowErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const canEdit = isPlatformUserAccountEditable(selectedUser?.status);

  const customerOptions = useMemo(
    () =>
      buildActiveCustomerSelectOptions({
        customers,
        selectedAccessRows: customerAccessRows,
      }),
    [customers, customerAccessRows]
  );

  useEffect(() => {
    if (!selectedUser) {
      return;
    }
    const platformRole =
      selectedUser.platformRole ?? PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
    const rows = (selectedUser.customerAccess ?? [])
      .map((access) => mapAccessToRow(access, roleOptions))
      .map((row) => enrichCustomerAccessRowFromActiveCustomer(row, customers));
    setFormValues({ platformRole });
    setCustomerAccessRows(rows);
    setBaseline({ platformRole, customerAccessRows: rows });
    setValidationError("");
    setRowErrors([]);
  }, [selectedUser, customers, roleOptions]);

  const clearValidation = () => {
    setValidationError("");
    setRowErrors([]);
  };

  if (!selectedUser) {
    return (
      <DetailPanelEmptyStateText>
        Select a user row to view and edit customer access.
      </DetailPanelEmptyStateText>
    );
  }

  const normalizeRoleValues = (roles) =>
    (roles ?? []).map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    );

  const normalizeCustomerAccessRows = () =>
    customerAccessRows
      .filter((row) => row.customerId)
      .map((row) => {
        const enriched = enrichCustomerAccessRowFromActiveCustomer(
          row,
          customers
        );
        return {
          accessId: enriched.accessId,
          customerId: enriched.customerId,
          roles: normalizeRoleValues(enriched.roles).filter(Boolean),
          accessStatus:
            enriched.accessStatus ?? CUSTOMER_ACCESS_STATUS.ACTIVE,
          customerCode: enriched.customerCode,
          customerName: enriched.customerName,
        };
      });

  const platformRoleChanged = hasPlatformRoleChanged(
    baseline.platformRole,
    formValues.platformRole
  );
  const customerAccessChanged = hasCustomerAccessChanged(
    baseline.customerAccessRows,
    customerAccessRows
  );
  const hasChanges = platformRoleChanged || customerAccessChanged;
  const saveDisabled = !canEdit || !hasChanges || saving;
  const activePlatformRole = normalizePlatformRoleValue(formValues.platformRole);
  const showPlatformAdminOptionalHint =
    shouldShowPlatformAdminOptionalCustomerHint(activePlatformRole);

  const handleSave = async () => {
    if (saveDisabled) {
      return;
    }
    const platformRole = normalizePlatformRoleValue(formValues.platformRole);
    const validation = validatePlatformCustomerAccess(
      customerAccessRows,
      platformRole
    );
    if (!validation.valid) {
      setValidationError(validation.formError ?? "");
      setRowErrors(validation.rowErrors);
      return;
    }
    clearValidation();
    setSaving(true);
    try {
      const success = await onSave({
        selectedUser,
        platformRoleChanged,
        customerAccessChanged,
        platformRole,
        customerAccessRows: normalizeCustomerAccessRows(),
      });
      if (success) {
        onCancel?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailPanelContainer>
      <DetailPanelBody>
        {!canEdit ? (
          <DetailPanelInlineAlert severity="info">
            {PLATFORM_USER_ACCESS_UI.saveDisabledNotActiveHint}
          </DetailPanelInlineAlert>
        ) : null}
        {validationError &&
        !isPlatformAdminPlatformRole(activePlatformRole) ? (
          <DetailPanelInlineAlert severity="error">
            {validationError}
          </DetailPanelInlineAlert>
        ) : null}
        <DetailPanelPlatformRoleSection>
          <GenericForm
            fields={platformFormFields(platformRoleOptions, !canEdit)}
            values={formValues}
            onChange={(name, value) => {
              clearValidation();
              setFormValues((previous) => ({ ...previous, [name]: value }));
            }}
            showBorder={false}
          />
        </DetailPanelPlatformRoleSection>
        <DetailPanelCustomerAccessSection>
          {showPlatformAdminOptionalHint ? (
            <DetailPanelInlineAlert severity="info">
              {PLATFORM_USER_ACCESS_UI.customerAccessOptionalWhenPlatformAdminHint}
            </DetailPanelInlineAlert>
          ) : null}
          <CustomerAccessEditor
            scrollableRows
            disabled={!canEdit}
            hideRemoveForPersistedRows
            rows={customerAccessRows}
            rowErrors={rowErrors}
            customerOptions={customerOptions}
            activeCustomers={customers}
            roleOptions={roleOptions}
            userId={selectedUser.id}
            onAddRow={() => {
              clearValidation();
              setCustomerAccessRows((previous) => [
                ...previous,
                createEmptyCustomerAccess(),
              ]);
            }}
            onRemoveRow={(index) => {
              clearValidation();
              setCustomerAccessRows((previous) =>
                previous.filter((_, currentIndex) => currentIndex !== index)
              );
            }}
            onRowChange={(index, name, value) => {
              clearValidation();
              setCustomerAccessRows((previous) =>
                previous.map((row, currentIndex) => {
                  if (currentIndex !== index) {
                    return row;
                  }
                  if (name === "customerId") {
                    const changed =
                      String(value ?? "") !== String(row.customerId ?? "");
                    const nextRow = changed
                      ? {
                          ...row,
                          customerId: value,
                          roles: [],
                          accessStatus: "",
                        }
                      : { ...row, customerId: value };
                    return enrichCustomerAccessRowFromActiveCustomer(
                      nextRow,
                      customers
                    );
                  }
                  return { ...row, [name]: value };
                })
              );
            }}
            onAccessActionSuccess={onAccessActionSuccess}
            onCustomerUserListAction={onCustomerUserListAction}
          />
        </DetailPanelCustomerAccessSection>
      </DetailPanelBody>
      <DetailPanelFooter>
        <Button onClick={onCancel}>{PLATFORM_USER_ACCESS_UI.cancelLabel}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {PLATFORM_USER_ACCESS_UI.saveChangesLabel}
        </Button>
      </DetailPanelFooter>
    </DetailPanelContainer>
  );
};

UserAccessDetailPanel.propTypes = {
  selectedUser: PropTypes.object,
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
  platformRoleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onAccessActionSuccess: PropTypes.func,
  onCustomerUserListAction: PropTypes.func,
};

export default UserAccessDetailPanel;
